/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';

export interface ICittaPaymentCreditCardTabContentProps {
    isBusy?: boolean;
    cardNameError?: boolean;
    cardNumError?: boolean;
    cardCvcError?: boolean;
    cardExpiryError?: boolean;
    ccMarkupData: ICCMarkupData;
    // tslint:disable-next-line:no-any
    handleCCFormSubmit (e:any):void;
    // tslint:disable-next-line:no-any
    handleCCInputChange (e:any):void;

    amexImageUrl: string;
    mastercardImageUrl: string;
    visaImageUrl: string;
}

export interface ICCMarkupData {
  Id: string;
  OrderNumber: string | undefined;
  guestCheckoutEmail: string;
  postForm: string;
  cvc: string;
  expiryMonth: string;
  expiryYear: number;
  name: string;
  number: string;
  sessionId: string;
}

const CittaPaymentCreditCardContentTab = ({
    isBusy,
    cardNameError,
    cardNumError,
    cardCvcError,
    cardExpiryError,
    ccMarkupData,
    handleCCFormSubmit,
    handleCCInputChange,
    amexImageUrl,
    mastercardImageUrl,
    visaImageUrl
}:ICittaPaymentCreditCardTabContentProps) => {
    const ccYears = generateExipiryYears();
    const callToActionProps = {
      className: 'btn btn-primary checkout-place-order__btn-place-order',
      id: 'confirm-purchase',
      glyph: isBusy ? 'spinner' : '',
      disabled: isBusy
    };

    return (
      <div className='cc'>
        <div className='payment-cc-container'>
          <form onSubmit={handleCCFormSubmit}>
            <div className={`form-group owner ${cardNameError === true ? 'error' : ''}`}>
              <label>Name</label>
              <input
                type='text'
                name='ccName'
                className='form-control'
                id='owner'
                aria-label='Name'
                placeholder='Name'
                onChange={handleCCInputChange}
              />
            </div>
            <div className={`form-group ${cardNumError === true ? 'error' : ''}`} id='card-number-field'>
              <label>Card Number</label>
              <input
                type='text'
                maxLength={16}
                name='ccNumber'
                className='form-control'
                aria-label='Card Number'
                placeholder='Card Number'
                id='cardNumber'
                onChange={handleCCInputChange}
              />
            </div>
            <div className={`form-group CVV ${cardCvcError === true ? 'error' : ''}`}>
              <label>CVC</label>
              <input
                type='text'
                maxLength={4}
                name='ccCvc'
                id='cvc'
                className='form-control'
                aria-label='CVC'
                placeholder='CVC'
                onChange={handleCCInputChange}
              />
            </div>
            <div className={`form-group ${cardExpiryError === true ? 'error' : ''}`} id='expiration-date'>
              <label>Expiration Date</label>
              <select
                name='ccExpiryMonth'
                onChange={handleCCInputChange}
                aria-label='Expiry Month'
                defaultValue='01'
              >
                <option aria-selected={true} value='01'>January</option>
                <option aria-selected={false} value='02'>February </option>
                <option aria-selected={false} value='03'>March</option>
                <option aria-selected={false} value='04'>April</option>
                <option aria-selected={false} value='05'>May</option>
                <option aria-selected={false} value='06'>June</option>
                <option aria-selected={false} value='07'>July</option>
                <option aria-selected={false} value='08'>August</option>
                <option aria-selected={false} value='09'>September</option>
                <option aria-selected={false} value='10'>October</option>
                <option aria-selected={false} value='11'>November</option>
                <option aria-selected={false} value='12'>December</option>
              </select>
              <select name='ccExpiryYear' aria-label='Expiry Year' onChange={handleCCInputChange}>
                {ccYears}
              </select>
            </div>
            <div className='form-group' id='credit_cards'>
              <img id='visa' alt='visa-logo' width='50px' src={visaImageUrl}/>
              <img id='mastercard' alt='master-logo' height='30px' src={mastercardImageUrl}/>
              <img id='amex' alt='amex-logo' height='40px' src={amexImageUrl}/>
            </div>
            <div className='form-group' id='pay-now'>
              <button
                {...callToActionProps}
                type='submit'
              >
                {isBusy ? 'Please wait ' : 'Place Order'}
              </button>
            </div>
          </form>
          {/* <div dangerouslySetInnerHTML={this._createCcMarkup()} /> */}
          <div>
            {createCcMarkup(ccMarkupData)}
          </div>
        </div>
      </div>
    );
};

// /**
//  * Markup for credit card form
//  */
// const createCcMarkup = (data: ICCMarkupData): { __html: string } => {
//   return {
//     __html: `<form method='post' enctype='multipart/form-data' action='${data.postForm}' style='display:none'>
//       <input type='text' name='CardNumber' value='${data.number}' />
//       <input type='text' name='ExpiryMonth' value='${data.expiryMonth}'/>
//       <input type='text' name='ExpiryYear' value='${data.expiryYear.toString().slice(-2)}' />
//       <input type='text' name='CardHolderName' value='${data.name}' />
//       <input type='text' name='Cvc2' value='${data.cvc}' />
//       <input type='hidden' name='SessionId' value='${data.sessionId}' />
//       <input type="text" name="UserTxnData1" value='${data.OrderNumber}' />
//       <input type="text" name="UserTxnData2" value='${data.Id}' />
//       <input type="text" name="UserTxnData3" value='${data.guestCheckoutEmail}' />
//       <input type='submit' value='Submit' id='ccButton' />
//       <input type='hidden' name='Action' value='Add' />
//       <input type='hidden' name='Object' value='DpsPxPay' />
//     </form>`};
// };

/**
 * Markup for credit card form
 */
const createCcMarkup = (data: ICCMarkupData): React.ReactNode => {
  const styleObj = {
    display: 'none'
  };
  return (
    <form method='post' encType='multipart/form-data' action={data.postForm} style={styleObj}>
      <input type='text' name='CardNumber' value={data.number} />
      <input type='text' name='ExpiryMonth' value={data.expiryMonth}/>
      <input type='text' name='ExpiryYear' value={data.expiryYear.toString().slice(-2)} />
      <input type='text' name='CardHolderName' value={data.name} />
      <input type='text' name='Cvc2' value={data.cvc} />
      <input type='hidden' name='SessionId' value={data.sessionId} />
      <input type='text' name='UserTxnData1' value={data.OrderNumber} />
      <input type='text' name='UserTxnData2' value={data.Id} />
      <input type='text' name='UserTxnData3' value={data.guestCheckoutEmail} />
      <input type='submit' value='Submit' id='ccButton' />
      <input type='hidden' name='Action' value='Add' />
      <input type='hidden' name='Object' value='DpsPxPay' />
    </form>
  );
};

/**
 * generate expiry years for credit card
 * return Mixed | Array
 */
const generateExipiryYears = () => {
  const date = new Date();
  const years = [];
  const year = date.getFullYear();
  for (let i = year; i < year + 7; i++) {
    years.push(
      <option aria-selected={i === year} value={i} key={i}>
        {i}
      </option>
    );
  }
  return years;
};

export default React.memo(CittaPaymentCreditCardContentTab);